import { Component, Input, OnInit } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import Task from 'src/models/task';
import { TaskEventType } from 'src/models/task-event';

type States = 'new' | 'active' | 'paused' | 'stopped';

@Component({
    selector: 'app-task-list-item',
    templateUrl: './task-list-item.component.html',
    styleUrls: ['./task-list-item.component.scss']
})
export class TaskListItemComponent implements OnInit {
    @Input() model!: Task;
    @Input() lineId!: string;
    @Input() isDisabled: boolean = false;

    isBusy = false;
    isErrored = false;
    private _state: States = 'new';
    get state(): States { return this._state }

    constructor(private api: ApiService) { }

    ngOnInit(): void {
        if (this.model) {
            this.refreshInnerState();
        }
    }

    private refreshInnerState() {
        const eventToStateMap: { [key in TaskEventType]: States } = {
            Pause: 'paused',
            Resume: 'active',
            Start: 'active',
            Stop: 'stopped'
        };
        const lastEventType = this.model.events[this.model.events.length - 1]?.type;
        this._state = eventToStateMap[lastEventType] || 'new';
    }

    get id(): string {
        return this.model.id;
    }
    get changeoverId(): string {
        return this.model.changeoverId;
    }
    get name(): string {
        return this.model.name;
    }
    get details(): string {
        return this.model.details;
    }

    get assignees(): string {
        return this.model.assignees.join(', ');
    }

    get canStart(): boolean {
        return this.state === 'new';
    }

    async start() {
        if (this.canStart) {
            this.isBusy = true;
            this.isErrored = false;
            try {
                await this.api.saveTaskEvent(this.lineId, this.model.changeoverId, this.model.id, 'Start');
                this._state = 'active';
            } catch {
                this.isErrored = true;
            }
            this.isBusy = false;
        }
    }

    get isActive(): boolean {
        return this.state === 'active';
    }

    get canPause(): boolean {
        return this.isActive;
    }

    async pause() {
        if (this.canPause) {
            this.isBusy = true;
            this.isErrored = false;
            try {
                await this.api.saveTaskEvent(this.lineId, this.model.changeoverId, this.model.id, 'Pause');
                this._state = 'paused';
            } catch {
                this.isErrored = true;
            }
            this.isBusy = false;
        }
    }

    get isPaused(): boolean {
        return this.state === 'paused';
    }

    get canResume(): boolean {
        return this.isPaused;
    }

    async resume() {
        if (this.canResume) {
            this.isBusy = true;
            this.isErrored = false;
            try {
                await this.api.saveTaskEvent(this.lineId, this.model.changeoverId, this.model.id, 'Resume');
                this._state = 'active';
            } catch {
                this.isErrored = true;
            }
            this.isBusy = false;
        }
    }

    get canStop(): boolean {
        return !this.isStopped && this.state !== 'new';
    }

    async stop() {
        if (this.canStop) {
            this.isBusy = true;
            this.isErrored = false;
            try {
                await this.api.saveTaskEvent(this.lineId, this.model.changeoverId, this.model.id, 'Stop');
                this._state = 'stopped';
            } catch {
                this.isErrored = true;
            }
            this.isBusy = false;
        }
    }

    get isStopped(): boolean {
        return this.state === 'stopped';
    }

    get canRestart(): boolean {
        return this.isStopped;
    }

    async restart() {
        if (this.canRestart) {
            this.isBusy = true;
            this.isErrored = false;
            try {
                await this.api.saveTaskEvent(this.lineId, this.model.changeoverId, this.model.id, 'Start');
                this._state = 'active';
            } catch {
                this.isErrored = true;
            }
            this.isBusy = false;
        }
    }

    refresh(): void {
        this.refreshInnerState();
        this.isBusy = false;
    }
}
